import React, { PureComponent } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";

const generateData = () => {
  const data = [];
  let healthRiskScore = 50;
  let engagementScore = 90;
  let sdohScore = 70;

  for (let i = 0; i < 20; i++) {
    healthRiskScore = Math.min(100, healthRiskScore + Math.random() * 4 - 1);
    engagementScore = Math.min(100, engagementScore + Math.random() * 4 - 1);
    sdohScore = Math.min(100, sdohScore + Math.random() * 4 - 1);

    data.push({
      date: `Q${(i % 4) + 1} ${2023 + Math.floor(i / 4)}`,
      healthRiskScore: parseFloat(healthRiskScore.toFixed(2)),
      engagementScore: parseFloat(engagementScore.toFixed(2)),
      sdohScore: parseFloat(sdohScore.toFixed(2)),
    });
  }
  return data;
};

const data: {
  date: string;
  healthRiskScore: number;
  engagementScore: number;
  sdohScore: number;
  [key: string]: any;
}[] = generateData();

export default class MemberInfoChart extends PureComponent {
  render() {
    const lines = [
      {
        key: "healthRiskScore",
        name: "Health Risk Score",
        strokeDasharray: "",
      },
      {
        key: "engagementScore",
        name: "Engagement Score",
        strokeDasharray: "10 10",
      },
      { key: "sdohScore", name: "SDoH Score", strokeDasharray: "1 3 1" },
    ];

    const renderCustomizedLabel = ({
      x,
      y,
      stroke,
      index,
      key,
    }: {
      x: string | number | undefined;
      y: number;
      stroke: string;
      index: number;
      key: string;
    }) => {
      if (index === data.length - 1) {
        const line = lines.find((line) => line.key === key) as {
          key: string;
          name: string;
          strokeDasharray: string;
        };
        const value = data[index][key];
        const roundedValue = Math.round(value);
        return (
          <text
            x={x ? Number(x) + 10 : 0} // Add a check for x to ensure it is not undefined
            y={y}
            dy={5}
            fill="#000"
            fontSize={16}
            textAnchor="start"
          >
            {line.name}
          </text>
        );
      }
      return null;
    };

    const yDomain = [
      (dataMin: number) => Math.floor(dataMin / 10) * 10,
      (dataMax: number) => Math.ceil(dataMax / 10) * 10,
    ];

    return (
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={data}
          margin={{
            top: 5,
            right: 200,
            left: 20,
            bottom: 5,
          }}
        >
          <defs>
            <linearGradient id="healthGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#3CD856" />
              <stop offset="30%" stopColor="#F2A142" />
              <stop offset="100%" stopColor="#EF2E51" />
            </linearGradient>
            <linearGradient id="engagementGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#EF2E51" />
              <stop offset="30%" stopColor="#F2A142" />
              <stop offset="100%" stopColor="#3CD856" />
            </linearGradient>
            <linearGradient id="sdohGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#3CD856" />
              <stop offset="30%" stopColor="#F2A142" />
              <stop offset="100%" stopColor="#EF2E51" />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 14 }} />
          <YAxis
            domain={yDomain as any}
            tickFormatter={(value) => `${Math.round(value)}%`}
          />
          <Tooltip />
          {lines.map((line) => (
            <Line
              key={line.key}
              type="monotone"
              dataKey={line.key}
              name={line.name}
              stroke={`url(#${
                line.key === "engagementScore"
                  ? "engagementGradient"
                  : line.key === "healthRiskScore"
                  ? "healthGradient"
                  : "sdohGradient"
              })`}
              strokeWidth={10}
              strokeDasharray={line.strokeDasharray}
              dot={false}
            >
              <LabelList
                dataKey={line.key}
                position="right"
                content={(props) =>
                  renderCustomizedLabel({ ...props, key: line.key } as any)
                }
              />
            </Line>
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }
}
